import { type FC, type ReactNode, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import PdpRespSimilarSoldItemsLoading from '../PdpRespSimilarSoldItems/PdpRespSimilarSoldItemsLoading.tsx';
import { useSimilarSoldItemsTracking } from '../hooks/useSimilarSoldItemsTracking.ts';
import { useShouldShowSimilarSoldItems } from '../hooks/useShouldShowSimilarSoldItems.ts';
import { useCurrency } from '../../shared/hooks/useCurrency.ts';
import { PdpRespAboutItemPanelItemModuleDescription } from './PdpRespAboutItemPanelItemModuleDescription.tsx';
import { PdpRespAboutItemPanelItemModuleDetails } from './PdpRespAboutItemPanelItemModuleDetails.tsx';

import { type PdpRespAboutItemPanelItemModule_viewer$key } from './__generated__/PdpRespAboutItemPanelItemModule_viewer.graphql.ts';
import { type PdpRespAboutItemPanelItemModule_item$key } from './__generated__/PdpRespAboutItemPanelItemModule_item.graphql.ts';
import { type PdpRespAboutItemPanelItemModule_user$key } from './__generated__/PdpRespAboutItemPanelItemModule_user.graphql.ts';

const PdpRespSimilarSoldItems = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSimilarSoldItems" */ '../PdpRespSimilarSoldItems/PdpRespSimilarSoldItems.tsx'
        )
);

type Props = {
    viewer: PdpRespAboutItemPanelItemModule_viewer$key;
    item: PdpRespAboutItemPanelItemModule_item$key;
    user: PdpRespAboutItemPanelItemModule_user$key | null | undefined;
    selectedSkuId: string | null;
    afterDisplay: boolean;
    dataTn?: string;
    children?: ReactNode;
    isAvailableItem?: boolean;
};

const viewerFragment = graphql`
    fragment PdpRespAboutItemPanelItemModule_viewer on Viewer {
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpRespSimilarSoldItems_viewer @include(if: $afterDisplay)
        }
        ...useShouldShowSimilarSoldItems_viewer
        ...useSimilarSoldItemsTracking_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespAboutItemPanelItemModule_item on Item {
        ...PdpRespAboutItemPanelItemModuleDescription_item
        ...PdpRespAboutItemPanelItemModuleDetails_item
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpRespSimilarSoldItemsLoading_item @include(if: $afterDisplay)
            ...PdpRespSimilarSoldItems_item @include(if: $afterDisplay)
        }
        ...useShouldShowSimilarSoldItems_item
    }
`;

const userFragment = graphql`
    fragment PdpRespAboutItemPanelItemModule_user on User {
        ...PdpRespAboutItemPanelItemModuleDetails_user
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpRespSimilarSoldItems_user @include(if: $afterDisplay)
        }
    }
`;

export const PdpRespAboutItemPanelItemModule: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    selectedSkuId,
    dataTn,
    afterDisplay,
    children,
    isAvailableItem,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { currency } = useCurrency();

    const {
        shouldShowSimilarSoldItemsModule,
        areEligibleConditionsMet,
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
    } = useShouldShowSimilarSoldItems({
        viewer: viewer || null,
        item: item || null,
        currency,
        afterDisplay,
    });

    const { trackSimilarSoldItemsModuleVisible } = useSimilarSoldItemsTracking({ viewer });

    return (
        <>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapSmall,
                    dibsCss.wFull
                )}
                data-tn={dataTn}
            >
                <PdpRespAboutItemPanelItemModuleDescription item={item} />
                <PdpRespAboutItemPanelItemModuleDetails
                    afterDisplay={afterDisplay}
                    item={item}
                    selectedSkuId={selectedSkuId}
                    user={user}
                />
                {children}
            </div>
            {afterDisplay && (
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible && isAvailableItem && areEligibleConditionsMet) {
                            trackSimilarSoldItemsModuleVisible({
                                isBelowMinPriceEstimate,
                                isBelowOrEqualToMaxPriceEstimate,
                            });
                        }
                    }}
                />
            )}
            {isAvailableItem && shouldShowSimilarSoldItemsModule && (
                <Suspense fallback={<PdpRespSimilarSoldItemsLoading item={item} />}>
                    <PdpRespSimilarSoldItems viewer={viewer} item={item} user={user} />
                </Suspense>
            )}
        </>
    );
};
